import { z } from 'zod';
import { emailSchema } from './common';
import { paletteSchema } from './manifest';
import { rankSchema, roleIdSchema, storedRankSchema } from './roles';
import { roleCategoryIdSchema } from './role-categories';
import { roleIssueSchema } from './role-rules';

export const LOGO_MAX_BYTES = 512 * 1024;
export const LOGO_MIME_TYPES = ['image/png', 'image/svg+xml'] as const;

/**
 * PUT /api/v1/admin/branding/logo is a multipart/form-data upload with the image in this field
 * (settled 2026-10-07 after the app sent a raw body and got 415).
 */
export const LOGO_UPLOAD_FIELD = 'logo';

/** GET/PUT /api/v1/admin/branding. Logo upload is a separate multipart field. */
export const brandingSchema = z.object({
  name: z.string().trim().min(1).max(120),
  shortName: z.string().trim().min(1).max(24),
  theme: z.object({ light: paletteSchema, dark: paletteSchema }),
});
export type Branding = z.infer<typeof brandingSchema>;

/**
 * Allowed email domains for email-code sign-in. Exact domains ("ashbury.example") or one
 * leading wildcard for subdomains ("*.ashbury.example"). Empty list = any domain.
 */
export const MAX_ALLOWED_EMAIL_DOMAINS = 50;

const domainLabel = '[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?';
export const emailDomainPatternSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(
    new RegExp(`^(\\*\\.)?(${domainLabel}\\.)+[a-z]{2,63}$`),
    'Enter a domain like school.edu or *.school.edu',
  );

const exactDomain = new RegExp(`^(${domainLabel}\\.)+[a-z]{2,63}$`);

/**
 * Google Workspace domain (the `hd` claim): one exact domain, no wildcard. Blank = none. Google
 * cannot be turned on without it (brief §7.2): it keeps personal and other schools' Google
 * accounts out.
 */
export const googleHostedDomainSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(253)
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .refine((v) => v === null || exactDomain.test(v), { message: 'Enter a domain like school.edu' });

export const GOOGLE_NEEDS_HOSTED_DOMAIN_MESSAGE =
  "Enter the school's Google Workspace domain before turning on Google. Only accounts from that domain can sign in.";

export const emailOtpSettingsSchema = z.object({
  enabled: z.boolean(),
  allowedDomains: z
    .array(emailDomainPatternSchema)
    .max(MAX_ALLOWED_EMAIL_DOMAINS)
    .default([])
    .transform((list) => [...new Set(list)]),
});
export type EmailOtpSettings = z.infer<typeof emailOtpSettingsSchema>;

/** True when the email's domain matches the list (empty list allows any domain). */
export function isEmailDomainAllowed(email: string, allowedDomains: readonly string[]): boolean {
  if (allowedDomains.length === 0) return true;
  const at = email.lastIndexOf('@');
  if (at < 1) return false;
  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase();
  return allowedDomains.some((pattern) =>
    pattern.startsWith('*.') ? domain.endsWith(pattern.slice(1)) : domain === pattern,
  );
}

/** Secrets are write-only: responses report only whether one is set. */
const secretStatus = z.object({ isSet: z.boolean() });

export const oidcProviderSettingsSchema = z.object({
  enabled: z.boolean(),
  clientId: z.string().max(200).nullable(),
  clientSecret: secretStatus,
});

export const authProvidersResponseSchema = z.object({
  google: oidcProviderSettingsSchema.extend({ hostedDomain: z.string().max(253).nullable() }),
  microsoft: oidcProviderSettingsSchema.extend({ entraTenantId: z.string().max(64).nullable() }),
  emailOtp: emailOtpSettingsSchema,
  smtp: z.object({
    host: z.string().max(253).nullable(),
    port: z.number().int().min(1).max(65535).nullable(),
    security: z.enum(['starttls', 'tls', 'none']),
    from: emailSchema.nullable(),
    username: z.string().max(200).nullable(),
    password: secretStatus,
  }),
  redirectUris: z.object({ google: z.string(), microsoft: z.string() }),
  autoEnroll: z
    .object({
      enabled: z.boolean(),
      defaultRoleId: z.string(),
      methods: z.object({ google: z.boolean(), microsoft: z.boolean(), emailOtp: z.boolean() }),
    })
    .default({
      enabled: false,
      defaultRoleId: 'user',
      methods: { google: false, microsoft: false, emailOtp: false },
    }),
});
export type AuthProvidersResponse = z.infer<typeof authProvidersResponseSchema>;

/** PUT body: secrets omitted = unchanged; provided = replaced. */
export const authProvidersUpdateSchema = z
  .object({
    google: z.object({
      enabled: z.boolean(),
      clientId: z.string().max(200).nullable(),
      clientSecret: z.string().min(1).max(500).optional(),
      hostedDomain: googleHostedDomainSchema,
    }),
    microsoft: z.object({
      enabled: z.boolean(),
      clientId: z.string().max(200).nullable(),
      clientSecret: z.string().min(1).max(500).optional(),
      entraTenantId: z.string().max(64).nullable(),
    }),
    emailOtp: emailOtpSettingsSchema,
    smtp: z.object({
      host: z.string().max(253).nullable(),
      port: z.number().int().min(1).max(65535).nullable(),
      security: z.enum(['starttls', 'tls', 'none']),
      from: emailSchema.nullable(),
      username: z.string().max(200).nullable(),
      password: z.string().min(1).max(500).optional(),
    }),
    autoEnroll: z
      .object({
        enabled: z.boolean(),
        defaultRoleId: z.string().regex(/^[a-z][a-z0-9_]{1,39}$/),
        methods: z.object({ google: z.boolean(), microsoft: z.boolean(), emailOtp: z.boolean() }),
      })
      .optional(),
  })
  .refine((v) => v.google.enabled || v.microsoft.enabled || v.emailOtp.enabled, {
    message: 'At least one login method must stay on',
  })
  .refine((v) => !v.google.enabled || v.google.hostedDomain !== null, {
    path: ['google', 'hostedDomain'],
    message: GOOGLE_NEEDS_HOSTED_DOMAIN_MESSAGE,
  });
export type AuthProvidersUpdate = z.infer<typeof authProvidersUpdateSchema>;

/** GET/PUT /api/v1/admin/modules. */
export const adminModuleSchema = z.object({
  id: z.string(),
  enabled: z.boolean(),
  title: z.string().min(1).max(60),
  order: z.number().int().min(0),
  config: z.record(z.string(), z.unknown()),
});
export const adminModulesSchema = z.object({ modules: z.array(adminModuleSchema) });
export type AdminModules = z.infer<typeof adminModulesSchema>;

/** /api/v1/admin/roles. */
export const roleSchema = z.object({
  id: roleIdSchema,
  name: z.string().trim().min(1).max(60),
  rank: storedRankSchema,
  /**
   * Role category (decision 2026-10-10 #5). Absent from servers before then: derive it with
   * roleCategoryOf(role).
   */
  category: roleCategoryIdSchema.optional(),
  permissions: z.array(z.string()),
  seeded: z.boolean(),
  memberCount: z.number().int().min(0),
});
export type Role = z.infer<typeof roleSchema>;

/** GET /api/v1/admin/roles. `issues`: the roles report (absent from older servers = none). */
export const roleListSchema = z.object({
  items: z.array(roleSchema),
  issues: z.array(roleIssueSchema).optional(),
});
export type RoleList = z.infer<typeof roleListSchema>;

export const createRoleSchema = z.object({
  name: z.string().trim().min(1).max(60),
  rank: rankSchema,
  /** Optional: defaults to the category the rank falls in. The rank must sit in its range. */
  category: roleCategoryIdSchema.optional(),
  permissions: z.array(z.string()),
});
export const updateRoleSchema = createRoleSchema.partial();
export type CreateRole = z.infer<typeof createRoleSchema>;
export type UpdateRole = z.infer<typeof updateRoleSchema>;

/** /api/v1/admin/users. */
export const userStatusSchema = z.enum(['active', 'invited', 'disabled']);
export const adminUserSchema = z.object({
  id: z.string().min(1),
  email: emailSchema,
  displayName: z.string(),
  roles: z.array(z.object({ id: z.string(), name: z.string() })),
  status: userStatusSchema,
  /** How the account was created; 'auto' shows the "Joined automatically" label. */
  source: z.enum(['invited', 'auto', 'seeded']).default('invited'),
  lastSignInAt: z.iso.datetime().nullable(),
});
export type AdminUser = z.infer<typeof adminUserSchema>;

export const inviteUserSchema = z.object({
  email: emailSchema,
  displayName: z.string().trim().min(1).max(120),
  roleIds: z.array(roleIdSchema).min(1),
});
export type InviteUser = z.infer<typeof inviteUserSchema>;

export const updateUserSchema = z.object({
  roleIds: z.array(roleIdSchema).optional(),
  disabled: z.boolean().optional(),
});
export type UpdateUser = z.infer<typeof updateUserSchema>;

export const pagedSchema = <T extends z.ZodType>(item: T) =>
  z.object({
    items: z.array(item),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    limit: z.number().int().min(1),
  });

/** POST /api/v1/admin/push/test. */
export const testPushSchema = z.object({ userId: z.string().min(1).nullable() });
export const testPushResultSchema = z.object({
  devices: z.number().int(),
  failed: z.number().int(),
  /**
   * Why sends failed, grouped by Expo error code (PUSH_ERROR_CODES) so the app can show a
   * friendly explanation. Absent from older servers.
   */
  issues: z.array(z.object({ code: z.string().min(1), count: z.number().int().min(1) })).optional(),
});
export type TestPushResult = z.infer<typeof testPushResultSchema>;

/** GET /api/v1/admin/audit. */
export const auditEntrySchema = z.object({
  id: z.string(),
  at: z.iso.datetime(),
  actor: z.string(),
  action: z.string(),
  target: z.string().nullable(),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

/** GET /api/v1/admin/permissions — catalogue plus categories so clients can group without hard-coding. */
export const permissionCatalogueResponseSchema = z.object({
  categories: z.array(
    z.object({
      id: z.string().min(1),
      group: z.enum(['admin', 'module']),
      title: z.string().min(1),
      description: z.string(),
      order: z.number().int(),
    }),
  ),
  permissions: z.array(
    z.object({
      id: z.string().min(1),
      group: z.enum(['admin', 'module']),
      category: z.string().min(1),
      description: z.string().min(1),
      tenantAdminOnly: z.boolean().optional(),
      /** Role categories that may hold it (absent from older servers = all). */
      allowedCategories: z.array(roleCategoryIdSchema).optional(),
    }),
  ),
});
export type PermissionCatalogueResponse = z.infer<typeof permissionCatalogueResponseSchema>;
